/**
 * AirspaceMapHero.jsx — v2
 * Hero for AirspaceMap product page — airspacemap.in
 * by Aviatricks Aero Labs
 *
 * Design: Shows a faithful browser mockup of the real airspacemap.in UI
 * — dark navbar, zone sidebar, India map with real zone categories.
 * Left side: headline + real feature highlights.
 * Right side: realistic app UI mockup.
 *
 * No Three.js. Pure React + CSS.
 * Place at: src/components/AirspaceMapHero.jsx
 */

import React, { useRef, useEffect, useState, useCallback } from 'react'

const BG = '#0a0f14'

/* ── SLIDES ──────────────────────────────────────────────────────── */
const SLIDES = [
    {
        num: '01',
        eyebrow: 'India Airspace Intelligence',
        heading: ['Know Every', 'Zone Before', 'You Fly'],
        body: 'India\'s most complete airspace map. Airports, military bases, green zones, and restricted airspace — all in one platform built for drone operators.',
        accent: '#ef4444',
        activeZone: 'Runway',
    },
    {
        num: '02',
        eyebrow: 'Military & Restricted Zones',
        heading: ['Avoid Fines,', 'Avoid Airspace', 'Violations'],
        body: 'Military bases, naval installations, abandoned airfields, and temporary red zones — clearly mapped so you never fly blind.',
        accent: '#f43f5e',
        activeZone: 'Military & Naval Base',
    },
    {
        num: '03',
        eyebrow: 'Green Zone Operations',
        heading: ['Find Where', 'You Can Fly', 'Legally'],
        body: 'Green zones mark DGCA-approved operational airspace. Filter by region, zoom into your city, and confirm your flight path before takeoff.',
        accent: '#22c55e',
        activeZone: 'Green Zone',
    },
    {
        num: '04',
        eyebrow: 'Permit Request System',
        heading: ['Request', 'Clearance', 'In Minutes'],
        body: 'Need to fly in a controlled zone? Submit permit requests directly through the platform. Built-in workflow for AAI and state authority approvals.',
        accent: '#f59e0b',
        activeZone: 'Helipad Base',
    },
    {
        num: '05',
        eyebrow: 'KML Import & Admin Tools',
        heading: ['Manage Your', 'Fleet\'s Full', 'Airspace Data'],
        body: 'Import KML files, define custom boundaries, manage feedback, and run an admin dashboard. Built for enterprise drone operators and authorities.',
        accent: '#3b82f6',
        activeZone: 'States Boundary Base',
    },
]

const N = SLIDES.length - 1
const SNAPS = SLIDES.map((_, i) => i / N)

function getTextSlide(p) {
    return Math.min(Math.floor(p * N + 0.001), N)
}

/* ── ZONE SIDEBAR DATA — matches real airspacemap.in ─────────────── */
const ZONES = [
    { label: 'Airports', color: '#1f2937', text: '#fff' },
    { label: 'Runway', color: '#ef4444', text: '#fff' },
    { label: 'Military & Naval Base', color: '#dc2626', text: '#fff' },
    { label: 'Abandoned', color: '#b91c1c', text: '#fff' },
    { label: 'Temporary Red Zone', color: '#ef4444', text: '#fff' },
    { label: 'Others Option', color: '#f87171', text: '#fff' },
    { label: 'Helipad Base', color: '#db2777', text: '#fff' },
    { label: 'Green Zone', color: '#16a34a', text: '#fff' },
    { label: 'Boundary Base', color: '#b45309', text: '#fff' },
    { label: 'States Boundary Base', color: '#d97706', text: '#fff' },
]

/* ── MAP MOCKUP ──────────────────────────────────────────────────── */
function AppMockup({ activeZone, accent }) {
    /* Dot positions roughly matching real airspacemap.in red dots */
    const dots = [
        { x: 42, y: 22, r: 2.5 }, // J&K
        { x: 50, y: 28, r: 2 },   // Himachal
        { x: 44, y: 34, r: 3 },   // Punjab/Delhi
        { x: 52, y: 35, r: 2.5 }, // UP North
        { x: 60, y: 30, r: 2 },   // Bihar
        { x: 65, y: 35, r: 3 },   // NE
        { x: 68, y: 42, r: 2 },   // Assam
        { x: 35, y: 42, r: 2.5 }, // Rajasthan
        { x: 42, y: 46, r: 2 },   // MP North
        { x: 55, y: 45, r: 3 },   // Jharkhand
        { x: 40, y: 55, r: 2.5 }, // Gujarat
        { x: 48, y: 52, r: 2 },   // MP South
        { x: 58, y: 55, r: 2 },   // Odisha
        { x: 44, y: 62, r: 3 },   // Mumbai
        { x: 50, y: 60, r: 2 },   // Maharashtra
        { x: 56, y: 63, r: 2.5 }, // AP North
        { x: 46, y: 70, r: 2 },   // Goa
        { x: 52, y: 70, r: 2.5 }, // TG
        { x: 55, y: 74, r: 3 },   // AP South
        { x: 48, y: 78, r: 2 },   // Kerala
        { x: 53, y: 80, r: 2 },   // Tamil Nadu
    ]

    const isRed = activeZone === 'Runway' || activeZone === 'Military & Naval Base' || activeZone === 'Abandoned' || activeZone === 'Temporary Red Zone' || activeZone === 'Others Option'
    const dotColor = isRed ? '#ef4444' : activeZone === 'Green Zone' ? '#22c55e' : activeZone === 'Helipad Base' ? '#db2777' : '#ef4444'

    return (
        <div className="amh-mockup">
            {/* Browser chrome */}
            <div className="amh-browser-bar">
                <div className="amh-browser-dots">
                    <span style={{ background: '#ef4444' }} />
                    <span style={{ background: '#f59e0b' }} />
                    <span style={{ background: '#22c55e' }} />
                </div>
                <div className="amh-browser-url">airspacemap.in</div>
            </div>

            {/* App navbar */}
            <div className="amh-app-nav">
                <div className="amh-app-logo">
                    <div className="amh-app-logo-icon">✈</div>
                </div>
                <div className="amh-app-title">India Airspacemap</div>
                <div className="amh-app-nav-btns">
                    <div className="amh-nav-pill" style={{ background: accent }}>Request Permit</div>
                    <div className="amh-nav-pill ghost">Feedback</div>
                </div>
            </div>

            {/* App body */}
            <div className="amh-app-body">
                {/* Left sidebar */}
                <div className="amh-sidebar">
                    {/* Search */}
                    <div className="amh-search">
                        <span>🔍</span>
                        <span className="amh-search-text">Search airports, cities...</span>
                    </div>

                    {/* Zones label */}
                    <div className="amh-zones-label">ZONES</div>

                    {/* Zone buttons */}
                    <div className="amh-zone-list">
                        {ZONES.map((z, i) => (
                            <div
                                key={i}
                                className="amh-zone-btn"
                                style={{
                                    background: z.label === activeZone
                                        ? z.color
                                        : `${z.color}cc`,
                                    color: z.text,
                                    transform: z.label === activeZone ? 'translateX(3px)' : 'none',
                                    boxShadow: z.label === activeZone
                                        ? `0 0 10px ${z.color}60`
                                        : 'none',
                                    fontWeight: z.label === activeZone ? 700 : 500,
                                }}
                            >
                                {z.label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Map area */}
                <div className="amh-map">
                    {/* Map tile background */}
                    <div className="amh-map-tiles" />

                    {/* Search bar on map */}
                    <div className="amh-map-search">
                        <span style={{ fontSize: 8 }}>🔍</span>
                        <span>Search airports, cities...</span>
                        <span style={{ marginLeft: 'auto', fontSize: 9 }}>📍</span>
                        <span style={{ fontSize: 9 }}>➤</span>
                    </div>

                    {/* Satellite toggle */}
                    <div className="amh-map-toggle">
                        <span className="amh-toggle-btn active">Street</span>
                        <span className="amh-toggle-btn">Satellite</span>
                    </div>

                    {/* Zoom controls */}
                    <div className="amh-zoom">
                        <div className="amh-zoom-btn">+</div>
                        <div className="amh-zoom-btn">−</div>
                        <div className="amh-zoom-btn" style={{ fontSize: 7 }}>⊕</div>
                    </div>

                    {/* SVG India map */}
                    <svg
                        className="amh-india-map"
                        viewBox="0 0 100 110"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        {/* India outline — simplified but recognisable */}
                        <path
                            d="
                              M38,4 L42,2 L47,3 L53,2 L58,4 L63,7 L67,11 L70,15
                              L73,19 L76,24 L78,29 L78,34 L80,38 L82,43 L82,48
                              L80,53 L77,57 L73,62 L70,67 L67,72 L64,77 L61,82
                              L58,86 L55,90 L52,94 L50,98 L48,94 L45,90 L42,86
                              L39,81 L36,75 L33,69 L30,63 L28,57 L26,51 L24,45
                              L22,39 L22,33 L23,28 L25,23 L28,18 L31,13 L35,8 Z
                            "
                            fill="rgba(34,197,94,0.18)"
                            stroke="#ef4444"
                            strokeWidth="0.7"
                            strokeOpacity="0.9"
                        />

                        {/* North East India bump */}
                        <path
                            d="M80,38 L84,36 L88,38 L90,42 L88,46 L85,48 L82,48"
                            fill="rgba(34,197,94,0.12)"
                            stroke="#ef4444"
                            strokeWidth="0.6"
                            strokeOpacity="0.7"
                        />

                        {/* Kashmir bump */}
                        <path
                            d="M38,4 L36,2 L34,4 L33,8 L35,8"
                            fill="rgba(34,197,94,0.12)"
                            stroke="#ef4444"
                            strokeWidth="0.5"
                            strokeOpacity="0.6"
                        />

                        {/* Sri Lanka */}
                        <ellipse cx="53" cy="104" rx="2.5" ry="3.5"
                            fill="rgba(34,197,94,0.1)"
                            stroke="rgba(0,0,0,0.3)"
                            strokeWidth="0.4"
                        />

                        {/* Red dots — facilities */}
                        {dots.map((d, i) => (
                            <circle
                                key={i}
                                cx={d.x}
                                cy={d.y}
                                r={d.r}
                                fill={dotColor}
                                opacity={isRed || activeZone === 'Green Zone' ? 0.85 : 0.4}
                                style={{ transition: 'all 0.5s ease' }}
                            />
                        ))}

                        {/* Location pin */}
                        <circle cx="50" cy="54" r="2.5" fill={accent} />
                        <circle cx="50" cy="54" r="4" fill="none" stroke={accent} strokeWidth="0.8" opacity="0.5" />

                        {/* State boundaries — faint lines */}
                        <path d="M38,4 L50,98" stroke="rgba(0,0,0,0.08)" strokeWidth="0.3" />
                        <path d="M33,35 L78,35" stroke="rgba(0,0,0,0.06)" strokeWidth="0.3" />
                        <path d="M28,55 L82,55" stroke="rgba(0,0,0,0.06)" strokeWidth="0.3" />
                        <path d="M30,70 L70,70" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />

                        {/* Green zone polygon (shown when green zone active) */}
                        {activeZone === 'Green Zone' && (
                            <polygon
                                points="40,58 55,54 62,62 56,72 42,70"
                                fill="rgba(34,197,94,0.25)"
                                stroke="#22c55e"
                                strokeWidth="0.6"
                                strokeDasharray="2 1"
                            />
                        )}

                        {/* KML polygon (shown for boundary) */}
                        {activeZone === 'States Boundary Base' && (
                            <>
                                <path d="M33,35 L78,35 L80,55 L28,55 Z"
                                    fill="rgba(217,119,6,0.12)"
                                    stroke="#d97706"
                                    strokeWidth="0.5"
                                    strokeDasharray="2 1.5"
                                />
                            </>
                        )}
                    </svg>

                    {/* Map attribution */}
                    <div className="amh-attribution">Powered by Aviatricks Aero Labs</div>
                </div>
            </div>
        </div>
    )
}

/* ── MAIN COMPONENT ──────────────────────────────────────────────── */
export default function AirspaceMapHero() {
    const wrapperRef = useRef()
    const pRef = useRef(0)

    const [progress, setProgress] = useState(0)
    const [slide, setSlide] = useState(0)
    const [textKey, setTextKey] = useState(0)
    const [mounted, setMounted] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [panelMode, setPanelMode] = useState('top')

    const targetRef = useRef(0)
    const animRaf = useRef(null)
    const resizeTimer = useRef(null)
    const touchRef = useRef({ startX: 0, startY: 0, startP: 0, active: false, isHoriz: null })

    const applyP = useCallback((p) => {
        pRef.current = p
        setProgress(p)
        setSlide(prev => {
            const next = getTextSlide(p)
            if (next !== prev) setTextKey(k => k + 1)
            return next
        })
    }, [])

    const animateTo = useCallback((target) => {
        targetRef.current = target
        if (animRaf.current) cancelAnimationFrame(animRaf.current)
        const tick = () => {
            const d = targetRef.current - pRef.current
            if (Math.abs(d) < 0.001) { applyP(targetRef.current); animRaf.current = null; return }
            applyP(pRef.current + d * 0.1)
            animRaf.current = requestAnimationFrame(tick)
        }
        animRaf.current = requestAnimationFrame(tick)
    }, [applyP])

    const goTo = useCallback((i) => animateTo(SNAPS[Math.max(0, Math.min(N, i))]), [animateTo])

    useEffect(() => {
        const check = () => {
            clearTimeout(resizeTimer.current)
            resizeTimer.current = setTimeout(() => setIsMobile(window.innerWidth < 900), 150)
        }
        setIsMobile(window.innerWidth < 900)
        window.addEventListener('resize', check)
        return () => { window.removeEventListener('resize', check); clearTimeout(resizeTimer.current) }
    }, [])

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 80)
        return () => { clearTimeout(t); if (animRaf.current) cancelAnimationFrame(animRaf.current) }
    }, [])

    /* Desktop scroll */
    useEffect(() => {
        if (isMobile) return
        const handler = () => {
            const el = wrapperRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const scrollable = el.offsetHeight - window.innerHeight
            if (scrollable <= 0) return
            if (rect.top > 0) { setPanelMode('top'); applyP(0) }
            else if (-rect.top >= scrollable) { setPanelMode('bottom'); applyP(1) }
            else { setPanelMode('fixed'); applyP(-rect.top / scrollable) }
        }
        window.addEventListener('scroll', handler, { passive: true })
        handler()
        return () => window.removeEventListener('scroll', handler)
    }, [isMobile, applyP])

    /* Mobile swipe */
    useEffect(() => {
        if (!isMobile) return
        const el = wrapperRef.current
        if (!el) return
        const onStart = (e) => {
            touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, startP: pRef.current, active: true, isHoriz: null }
        }
        const onMove = (e) => {
            if (!touchRef.current.active) return
            const dx = e.touches[0].clientX - touchRef.current.startX
            const dy = e.touches[0].clientY - touchRef.current.startY
            if (touchRef.current.isHoriz === null) {
                if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return
                touchRef.current.isHoriz = Math.abs(dx) >= Math.abs(dy)
            }
            if (!touchRef.current.isHoriz) return
            e.preventDefault()
            applyP(Math.max(0, Math.min(1, touchRef.current.startP + (-dx / (window.innerWidth * 0.75)))))
        }
        const onEnd = () => {
            if (!touchRef.current.active) return
            touchRef.current.active = false
            animateTo(SNAPS.reduce((a, b) => Math.abs(b - pRef.current) < Math.abs(a - pRef.current) ? b : a))
        }
        el.addEventListener('touchstart', onStart, { passive: true })
        el.addEventListener('touchmove', onMove, { passive: false })
        el.addEventListener('touchend', onEnd, { passive: true })
        return () => {
            el.removeEventListener('touchstart', onStart)
            el.removeEventListener('touchmove', onMove)
            el.removeEventListener('touchend', onEnd)
        }
    }, [isMobile, applyP, animateTo])

    const cur = SLIDES[slide]
    const panelStyle = isMobile ? {} : {
        position: panelMode === 'fixed' ? 'fixed' : 'absolute',
        top: panelMode === 'bottom' ? 'auto' : 0,
        bottom: panelMode === 'bottom' ? 0 : 'auto',
        left: 0, right: 0,
    }

    return (
        <>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap" />
            <style>{CSS}</style>

            <div ref={wrapperRef} className={`amh-wr${isMobile ? ' mob' : ''}`}>
                <div className="amh-panel" style={panelStyle}>

                    {/* Subtle grid bg */}
                    <div className="amh-bg-grid" />
                    <div
                        className="amh-bg-glow"
                        style={{ background: `radial-gradient(ellipse 55% 60% at 75% 50%, ${cur.accent}10 0%, transparent 70%)` }}
                    />

                    {/* Brand */}
                    <div className="amh-brand">
                        <div className="amh-brand-sub">Aviatricks Aero Labs</div>
                        <div className="amh-brand-name">AIRSPACEMAP</div>
                    </div>

                    {/* Main layout */}
                    <div className="amh-layout">

                        {/* LEFT — text */}
                        <div className="amh-left">
                            <div className="amh-text" key={textKey}>
                                <div className="amh-slide-num" style={{ color: cur.accent }}>
                                    <span className="amh-num-val">{cur.num}</span>
                                    <span className="amh-num-total">/ 05</span>
                                </div>
                                <div
                                    className="amh-eyebrow"
                                    style={{ color: cur.accent, borderColor: `${cur.accent}30`, background: `${cur.accent}10` }}
                                >
                                    {cur.eyebrow}
                                </div>
                                <h2 className="amh-h2">
                                    {cur.heading.map((line, i) => (
                                        <span key={i}>{line}</span>
                                    ))}
                                </h2>
                                <p className="amh-body">{cur.body}</p>
                                <div className="amh-actions">
                                    <a
                                        href="https://airspacemap.in"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="amh-cta-primary"
                                        style={{ background: cur.accent, boxShadow: `0 8px 24px ${cur.accent}35` }}
                                    >
                                        Open AirspaceMap
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                                        </svg>
                                    </a>
                                    <a
                                        href="https://airspacemap.in"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="amh-cta-ghost"
                                    >
                                        Request Permit
                                    </a>
                                </div>
                            </div>

                            {/* Feature tags */}
                            <div className="amh-tags">
                                {['Interactive Map', 'DGCA Zones', 'Permit Portal', 'KML Import', 'Admin Dashboard', 'Feedback System'].map((tag, i) => (
                                    <div key={i} className="amh-tag">
                                        <span className="amh-tag-dot" style={{ background: cur.accent }} />
                                        {tag}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT — app mockup */}
                        <div className="amh-right">
                            {mounted && <AppMockup activeZone={cur.activeZone} accent={cur.accent} />}
                        </div>
                    </div>

                    {/* Slide dots */}
                    <div className="amh-dots">
                        {SLIDES.map((s, i) => (
                            <div
                                key={i}
                                role="button"
                                aria-label={`Slide ${i + 1}`}
                                className={`amh-dot${slide === i ? ' on' : ''}`}
                                style={{ background: slide === i ? cur.accent : 'rgba(255,255,255,.18)' }}
                                onClick={() => goTo(i)}
                            />
                        ))}
                    </div>

                    {/* Counter */}
                    <div className="amh-counter">
                        <strong style={{ color: 'rgba(255,255,255,.55)' }}>{String(slide + 1).padStart(2, '0')}</strong>
                        <span> / 05</span>
                    </div>

                    {/* Scroll hint */}
                    <div className="amh-hint" style={{ opacity: progress < 0.04 ? 1 : 0 }}>
                        <span>{isMobile ? 'Swipe to explore' : 'Scroll to explore'}</span>
                        <div className="amh-hint-line" />
                    </div>

                    {/* Mobile nav buttons */}
                    <div className="amh-nav">
                        <button className="amh-nav-btn" onClick={() => goTo(slide - 1)} disabled={slide === 0}
                            style={{ borderColor: cur.accent, opacity: slide === 0 ? 0.3 : 1 }} aria-label="Prev">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cur.accent} strokeWidth="2.5">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button className="amh-nav-btn" onClick={() => goTo(slide + 1)} disabled={slide === N}
                            style={{ borderColor: cur.accent, opacity: slide === N ? 0.3 : 1 }} aria-label="Next">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cur.accent} strokeWidth="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Progress bar */}
                    <div className="amh-pbar">
                        <div className="amh-pfill" style={{ width: `${progress * 100}%`, background: cur.accent }} />
                    </div>

                </div>
            </div>
        </>
    )
}

/* ── CSS ─────────────────────────────────────────────────────────── */
const CSS = `
/* ── Wrapper ── */
.amh-wr { position:relative; height:600vh; background:${BG}; }
.amh-wr.mob { height:100vh; }
.amh-panel { width:100%; height:100vh; overflow:hidden; background:${BG}; }
.amh-wr.mob .amh-panel { position:relative !important; top:auto !important; bottom:auto !important; }

/* ── Background ── */
.amh-bg-grid {
  position:absolute; inset:0; pointer-events:none; z-index:0;
  background-image:
    linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px);
  background-size:48px 48px;
}
.amh-bg-glow {
  position:absolute; inset:0; pointer-events:none; z-index:1;
  transition: background 0.8s ease;
}

/* ── Brand ── */
.amh-brand {
  position:absolute; top:26px; left:40px; z-index:20;
}
.amh-brand-sub {
  font-family:'JetBrains Mono', monospace;
  font-size:9px; letter-spacing:.2em; color:rgba(255,255,255,.25);
  text-transform:uppercase; margin-bottom:3px;
}
.amh-brand-name {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:15px; font-weight:800; color:#fff; letter-spacing:.08em;
}

/* ── Layout ── */
.amh-layout {
  position:absolute; inset:0; z-index:10;
  display:grid; grid-template-columns:1fr 1.15fr;
  align-items:center; padding:64px 40px 40px 40px; gap:48px;
}

/* ── Left ── */
.amh-left {
  display:flex; flex-direction:column; gap:28px;
}
.amh-text {
  display:flex; flex-direction:column; gap:16px;
  animation:amhIn .5s cubic-bezier(.22,1,.36,1) both;
}
@keyframes amhIn { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }

.amh-slide-num {
  display:flex; align-items:baseline; gap:6px;
  font-family:'JetBrains Mono', monospace;
}
.amh-num-val { font-size:13px; font-weight:500; letter-spacing:.1em; }
.amh-num-total { font-size:10px; color:rgba(255,255,255,.2); }

.amh-eyebrow {
  display:inline-block; width:fit-content;
  font-family:'JetBrains Mono', monospace;
  font-size:10px; font-weight:500; letter-spacing:.15em;
  text-transform:uppercase; padding:5px 12px; border-radius:4px;
  border:1px solid; transition:all 0.5s ease;
}

.amh-h2 {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:clamp(28px,3.2vw,52px); font-weight:800;
  line-height:1.06; color:#fff; margin:0;
}
.amh-h2 span { display:block; }

.amh-body {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:14px; color:rgba(255,255,255,.42);
  line-height:1.85; max-width:400px; margin:0; font-weight:400;
}

.amh-actions {
  display:flex; gap:12px; flex-wrap:wrap; align-items:center;
}
.amh-cta-primary {
  display:inline-flex; align-items:center; gap:8px;
  padding:11px 22px; border-radius:7px;
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:13px; font-weight:700; color:#fff;
  text-decoration:none;
  transition:transform .2s, box-shadow .2s;
}
.amh-cta-primary:hover { transform:translateY(-2px); }
.amh-cta-ghost {
  display:inline-flex; align-items:center;
  padding:11px 22px; border-radius:7px;
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:13px; font-weight:600; color:rgba(255,255,255,.6);
  text-decoration:none;
  border:1px solid rgba(255,255,255,.12);
  transition:border-color .2s, color .2s;
}
.amh-cta-ghost:hover { border-color:rgba(255,255,255,.3); color:#fff; }

/* Feature tags */
.amh-tags {
  display:flex; flex-wrap:wrap; gap:8px;
}
.amh-tag {
  display:flex; align-items:center; gap:6px;
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:11px; font-weight:500; color:rgba(255,255,255,.4);
  padding:4px 10px;
  background:rgba(255,255,255,.04);
  border:1px solid rgba(255,255,255,.07);
  border-radius:20px;
  transition:color .2s, border-color .2s;
}
.amh-tag:hover { color:rgba(255,255,255,.7); border-color:rgba(255,255,255,.15); }
.amh-tag-dot { width:5px; height:5px; border-radius:50%; flex-shrink:0; }

/* ── RIGHT mockup ── */
.amh-right {
  display:flex; align-items:center; justify-content:center;
  height:100%;
}

/* ── APP MOCKUP ── */
.amh-mockup {
  width:100%; max-width:560px;
  border-radius:10px; overflow:hidden;
  border:1px solid rgba(255,255,255,.1);
  box-shadow:
    0 40px 80px rgba(0,0,0,.6),
    0 0 0 1px rgba(255,255,255,.05);
  background:#fff;
  display:flex; flex-direction:column;
  height: clamp(400px, 58vh, 520px);
}

/* Browser bar */
.amh-browser-bar {
  display:flex; align-items:center; gap:10px;
  padding:8px 14px;
  background:#1c1c1e;
  border-bottom:1px solid rgba(255,255,255,.06);
  flex-shrink:0;
}
.amh-browser-dots {
  display:flex; gap:5px;
}
.amh-browser-dots span {
  width:10px; height:10px; border-radius:50%; display:block;
}
.amh-browser-url {
  flex:1; text-align:center;
  font-family:'JetBrains Mono', monospace;
  font-size:10px; color:rgba(255,255,255,.4);
  background:rgba(255,255,255,.07);
  padding:3px 12px; border-radius:4px; letter-spacing:.04em;
}

/* App navbar */
.amh-app-nav {
  display:flex; align-items:center; gap:10px;
  padding:8px 14px;
  background:#111827;
  flex-shrink:0;
}
.amh-app-logo {
  width:26px; height:26px; border-radius:6px;
  background:rgba(255,255,255,.1);
  display:flex; align-items:center; justify-content:center;
  font-size:12px;
}
.amh-app-title {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:12px; font-weight:700; color:#fff;
  flex:1; text-align:center;
}
.amh-app-nav-btns {
  display:flex; gap:6px;
}
.amh-nav-pill {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:9px; font-weight:600; color:#fff;
  padding:4px 10px; border-radius:4px;
  white-space:nowrap; transition:all 0.4s ease;
}
.amh-nav-pill.ghost {
  background:rgba(255,255,255,.1);
  border:1px solid rgba(255,255,255,.15);
}

/* App body */
.amh-app-body {
  display:flex; flex:1; min-height:0; overflow:hidden;
}

/* Sidebar */
.amh-sidebar {
  width:140px; flex-shrink:0;
  background:#f9fafb;
  border-right:1px solid #e5e7eb;
  display:flex; flex-direction:column;
  overflow:hidden;
}
.amh-search {
  display:flex; align-items:center; gap:5px;
  margin:8px; padding:5px 8px;
  background:#fff; border:1px solid #e5e7eb;
  border-radius:5px;
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:8px; color:#9ca3af; flex-shrink:0;
}
.amh-search-text { font-size:7.5px; }
.amh-zones-label {
  font-family:'JetBrains Mono', monospace;
  font-size:8px; letter-spacing:.15em; color:#9ca3af;
  padding:4px 10px 6px; flex-shrink:0;
}
.amh-zone-list {
  display:flex; flex-direction:column; gap:2px;
  padding:0 6px 6px; overflow-y:auto; flex:1;
}
.amh-zone-btn {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:8.5px; font-weight:500;
  padding:5px 8px; border-radius:4px;
  cursor:pointer; flex-shrink:0;
  transition:transform 0.3s ease, box-shadow 0.3s ease, font-weight 0.2s;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}

/* Map area */
.amh-map {
  flex:1; position:relative; overflow:hidden;
  background:#e8f0e8;
}
.amh-map-tiles {
  position:absolute; inset:0;
  background-image:
    linear-gradient(rgba(0,0,0,.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,.03) 1px, transparent 1px);
  background-size:20px 20px;
  background-color:#e8f0e8;
}
.amh-map-search {
  position:absolute; top:8px; left:8px; right:55px;
  display:flex; align-items:center; gap:5px;
  background:#fff; border:1px solid #ddd; border-radius:4px;
  padding:4px 8px; z-index:2;
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:8px; color:#9ca3af;
  box-shadow:0 1px 4px rgba(0,0,0,.1);
}
.amh-map-toggle {
  position:absolute; top:8px; right:8px; z-index:2;
  display:flex; border:1px solid #ddd; border-radius:4px;
  overflow:hidden; background:#fff;
  box-shadow:0 1px 4px rgba(0,0,0,.1);
}
.amh-toggle-btn {
  font-family:'Plus Jakarta Sans', sans-serif;
  font-size:8px; font-weight:500;
  padding:4px 8px; color:#6b7280; cursor:pointer;
}
.amh-toggle-btn.active {
  background:#1d4ed8; color:#fff;
}
.amh-zoom {
  position:absolute; right:8px; top:36px; z-index:2;
  display:flex; flex-direction:column;
  background:#fff; border:1px solid #ddd; border-radius:4px;
  overflow:hidden; box-shadow:0 1px 4px rgba(0,0,0,.1);
}
.amh-zoom-btn {
  font-size:12px; font-weight:600; color:#374151;
  padding:4px 8px; text-align:center; cursor:pointer;
  border-bottom:1px solid #f3f4f6;
  font-family:'Plus Jakarta Sans', sans-serif;
}
.amh-zoom-btn:last-child { border-bottom:none; }
.amh-india-map {
  position:absolute;
  top:5%; left:20%;
  width:65%; height:88%;
}
.amh-attribution {
  position:absolute; bottom:0; left:0; right:0;
  font-family:'JetBrains Mono', monospace;
  font-size:7px; color:rgba(0,0,0,.4);
  text-align:center; padding:3px;
  background:rgba(255,255,255,.7);
  letter-spacing:.06em;
}

/* ── Navigation ── */
.amh-dots {
  position:absolute; right:18px; top:50%;
  transform:translateY(-50%); z-index:20;
  display:flex; flex-direction:column; gap:10px;
}
.amh-dot {
  width:5px; height:5px; border-radius:50%;
  cursor:pointer; transition:transform .3s, background .4s;
}
.amh-dot.on { transform:scale(2.2); }

.amh-counter {
  position:absolute; bottom:24px; left:40px; z-index:20;
  font-family:'JetBrains Mono', monospace;
  font-size:10px; color:rgba(255,255,255,.16); letter-spacing:.14em;
}

.amh-hint {
  position:absolute; bottom:24px; left:50%;
  transform:translateX(-50%); z-index:20;
  display:flex; flex-direction:column; align-items:center; gap:6px;
  pointer-events:none; transition:opacity .7s;
}
.amh-hint span {
  font-family:'JetBrains Mono', monospace;
  font-size:8px; letter-spacing:.2em; color:rgba(255,255,255,.18);
  text-transform:uppercase; white-space:nowrap;
}
.amh-hint-line {
  width:1px; height:24px;
  background:linear-gradient(to bottom, rgba(255,255,255,.25), transparent);
  animation:amh-ln 1.9s ease-in-out infinite;
}
@keyframes amh-ln {
  0%,100%{opacity:.35;transform:scaleY(1)}
  50%{opacity:1;transform:scaleY(.5)}
}

.amh-pbar {
  position:absolute; bottom:0; left:0; right:0;
  height:2px; background:rgba(255,255,255,.05); z-index:20;
}
.amh-pfill { height:100%; transition:width .08s linear, background .6s; }

.amh-nav { display:none; }

/* ── Mobile ── */
@media(max-width:899px){
  .amh-brand { left:20px; top:20px; }
  .amh-layout {
    grid-template-columns:1fr;
    padding:0; gap:0; align-items:flex-end;
  }
  .amh-left {
    order:2; gap:16px;
    padding:0 20px 88px; z-index:10; position:relative;
    background:linear-gradient(to top, ${BG} 55%, rgba(10,15,20,.7) 82%, transparent);
  }
  .amh-right {
    order:1; position:absolute; inset:0;
  }
  .amh-mockup {
    max-width:none; height:100%; border-radius:0; border:none;
    box-shadow:none;
  }
  .amh-h2 { font-size:clamp(22px,6.5vw,32px); }
  .amh-body { font-size:13px; max-width:none; }
  .amh-tags { display:none; }
  .amh-dots { right:12px; }
  .amh-counter { left:20px; bottom:12px; }
  .amh-hint { bottom:12px; }
  .amh-nav {
    display:flex; position:absolute;
    bottom:58px; right:16px; gap:10px; z-index:30;
  }
  .amh-nav-btn {
    width:40px; height:40px; border-radius:50%;
    border:1.5px solid; background:rgba(10,15,20,.75);
    display:flex; align-items:center; justify-content:center;
    cursor:pointer; -webkit-tap-highlight-color:transparent;
    transition:background .18s;
  }
  .amh-nav-btn:not(:disabled):active { background:rgba(255,255,255,.1); }
}
@media(max-width:400px){
  .amh-left { padding:0 16px 80px; }
  .amh-h2 { font-size:clamp(20px,6vw,26px); }
}
`